import { sortByDescendingShortSide, getPossibleOrientations, applySortStrategy } from './common.js';

/**
 * Split rules for guillotine cutting
 */
export const SPLIT_RULES = {
  SAS: 'SAS',     // Shorter Axis Split (original)
  MINAS: 'MINAS', // Minimize Area of Smaller resulting free rect
  SLAS: 'SLAS',   // Shorter Leftover Axis Split
};

/**
 * Decide whether to split horizontally based on the split rule.
 * "Split horizontally" means the horizontal cut goes full-width and the
 * right free rect gets the full remaining height.
 */
const shouldSplitHorizontally = (rect, orientation, cutWidth, splitRule) => {
  const rightWidth = rect.width - orientation.width - cutWidth;
  const bottomHeight = rect.height - orientation.height - cutWidth;

  switch (splitRule) {
    case SPLIT_RULES.MINAS: {
      // Split to maximize the smaller resulting free rect's area
      // Option A (horizontal/right gets full height): right = rightWidth * rect.height, bottom = orientation.width * bottomHeight
      // Option B (vertical/bottom gets full width): right = rightWidth * orientation.height, bottom = rect.width * bottomHeight
      const aSmaller = Math.min(rightWidth * rect.height, orientation.width * bottomHeight);
      const bSmaller = Math.min(rightWidth * orientation.height, rect.width * bottomHeight);
      return aSmaller > bSmaller;
    }
    case SPLIT_RULES.SLAS: {
      // Split so the shorter leftover dimension determines axis
      // If the leftover width is shorter, split vertically (bottom gets full width)
      // If the leftover height is shorter, split horizontally (right gets full height)
      return bottomHeight < rightWidth;
    }
    case SPLIT_RULES.SAS:
    default:
      // Shorter Axis Split: split along shorter leftover axis
      return rightWidth < bottomHeight;
  }
};

/**
 * Rectangle Merge: Merge adjacent free rectangles that share an edge.
 * Uses edge-keyed maps for O(n) per pass instead of O(n^2).
 */
const mergeAdjacentRects = (freeRects, cutWidth) => {
  const EPS = 0.001;
  const roundKey = (v) => Math.round(v * 1000);

  let merged = true;
  while (merged) {
    merged = false;

    // Build map: right-edge key -> rect index (for horizontal merging)
    // Key = "y,height,rightX" where rightX = x + width + cutWidth
    const rightEdgeMap = new Map();
    // Build map: bottom-edge key -> rect index (for vertical merging)
    // Key = "x,width,bottomY" where bottomY = y + height + cutWidth
    const bottomEdgeMap = new Map();

    for (let i = 0; i < freeRects.length; i++) {
      const r = freeRects[i];
      const rKey = `${roundKey(r.y)},${roundKey(r.height)},${roundKey(r.x + r.width + cutWidth)}`;
      rightEdgeMap.set(rKey, i);
      const bKey = `${roundKey(r.x)},${roundKey(r.width)},${roundKey(r.y + r.height + cutWidth)}`;
      bottomEdgeMap.set(bKey, i);
    }

    const removed = new Set();

    for (let i = 0; i < freeRects.length; i++) {
      if (removed.has(i)) continue;
      const r = freeRects[i];

      // Check if there's a rect whose right edge meets our left edge (horizontal merge)
      const hKey = `${roundKey(r.y)},${roundKey(r.height)},${roundKey(r.x)}`;
      if (rightEdgeMap.has(hKey)) {
        const j = rightEdgeMap.get(hKey);
        if (j !== i && !removed.has(j)) {
          const left = freeRects[j];
          freeRects[i] = { x: left.x, y: left.y, width: left.width + cutWidth + r.width, height: r.height };
          removed.add(j);
          merged = true;
          continue;
        }
      }

      // Check if there's a rect whose bottom edge meets our top edge (vertical merge)
      const vKey = `${roundKey(r.x)},${roundKey(r.width)},${roundKey(r.y)}`;
      if (bottomEdgeMap.has(vKey)) {
        const j = bottomEdgeMap.get(vKey);
        if (j !== i && !removed.has(j)) {
          const top = freeRects[j];
          freeRects[i] = { x: top.x, y: top.y, width: r.width, height: top.height + cutWidth + r.height };
          removed.add(j);
          merged = true;
          continue;
        }
      }
    }

    if (removed.size > 0) {
      // Filter out removed rects
      const kept = [];
      for (let i = 0; i < freeRects.length; i++) {
        if (!removed.has(i)) kept.push(freeRects[i]);
      }
      freeRects.length = 0;
      for (const r of kept) freeRects.push(r);
    }
  }
  return freeRects;
};

/**
 * Try to place a piece on a sheet's free rects using BSSF.
 * Returns { placement, rectIndex, orientation } or null.
 */
const findGuillotinePlacement = (piece, freeRects) => {
  const orientations = getPossibleOrientations(piece);
  let bestPlacement = null, bestRectIndex = -1, bestShortSide = Infinity, bestLongSide = Infinity, bestOrientation = null;

  for (const orientation of orientations) {
    for (let j = 0; j < freeRects.length; j++) {
      const rect = freeRects[j];
      if (orientation.width <= rect.width && orientation.height <= rect.height) {
        const leftoverH = rect.width - orientation.width;
        const leftoverV = rect.height - orientation.height;
        const shortSide = Math.min(leftoverH, leftoverV);
        const longSide = Math.max(leftoverH, leftoverV);
        if (shortSide < bestShortSide || (shortSide === bestShortSide && longSide < bestLongSide)) {
          bestShortSide = shortSide; bestLongSide = longSide;
          bestRectIndex = j; bestOrientation = orientation;
          bestPlacement = { x: rect.x, y: rect.y };
        }
      }
    }
  }

  if (bestPlacement && bestOrientation) {
    return { placement: bestPlacement, rectIndex: bestRectIndex, orientation: bestOrientation };
  }
  return null;
};

/**
 * Place a piece on a guillotine sheet, splitting the free rect.
 */
const placeOnGuillotineSheet = (sheet, piece, rectIndex, orientation, cutWidth, splitRule = SPLIT_RULES.SAS) => {
  sheet.pieces.push({ ...orientation, x: piece.x, y: piece.y });

  const rect = sheet.freeRects[rectIndex];
  sheet.freeRects.splice(rectIndex, 1);

  const rightWidth = rect.width - orientation.width - cutWidth;
  const bottomHeight = rect.height - orientation.height - cutWidth;

  if (rightWidth > cutWidth && bottomHeight > cutWidth) {
    if (shouldSplitHorizontally(rect, orientation, cutWidth, splitRule)) {
      sheet.freeRects.push({ x: rect.x + orientation.width + cutWidth, y: rect.y, width: rightWidth, height: rect.height });
      sheet.freeRects.push({ x: rect.x, y: rect.y + orientation.height + cutWidth, width: orientation.width, height: bottomHeight });
    } else {
      sheet.freeRects.push({ x: rect.x, y: rect.y + orientation.height + cutWidth, width: rect.width, height: bottomHeight });
      sheet.freeRects.push({ x: rect.x + orientation.width + cutWidth, y: rect.y, width: rightWidth, height: orientation.height });
    }
  } else if (rightWidth > cutWidth) {
    sheet.freeRects.push({ x: rect.x + orientation.width + cutWidth, y: rect.y, width: rightWidth, height: rect.height });
  } else if (bottomHeight > cutWidth) {
    sheet.freeRects.push({ x: rect.x, y: rect.y + orientation.height + cutWidth, width: rect.width, height: bottomHeight });
  }

  sheet.freeRects = mergeAdjacentRects(sheet.freeRects, cutWidth);
};

/**
 * Guillotine BSSF-SAS-RM algorithm with multi-sheet backfill
 */
export const guillotineOptimize = (allPieces, sheetWidth, sheetHeight, cutWidth, sortStrategy, splitRule = SPLIT_RULES.SAS) => {
  const sheets = []; // Each: { id, width, height, pieces, freeRects }
  let remainingPieces = sortStrategy ? applySortStrategy(allPieces, sortStrategy) : sortByDescendingShortSide(allPieces);

  while (remainingPieces.length > 0) {
    // Open a new sheet
    const sheet = {
      id: sheets.length + 1, width: sheetWidth, height: sheetHeight, pieces: [],
      freeRects: [{ x: 0, y: 0, width: sheetWidth, height: sheetHeight }]
    };
    const placedIndices = new Set();

    // Fill the new sheet greedily
    for (let i = 0; i < remainingPieces.length; i++) {
      if (placedIndices.has(i)) continue;
      const result = findGuillotinePlacement(remainingPieces[i], sheet.freeRects);
      if (result) {
        placeOnGuillotineSheet(sheet, result.placement, result.rectIndex, result.orientation, cutWidth, splitRule);
        placedIndices.add(i);
      }
    }

    remainingPieces = remainingPieces.filter((_, i) => !placedIndices.has(i));
    if (sheet.pieces.length > 0) sheets.push(sheet);
    else break;

    // BACKFILL: try remaining pieces on ALL previous sheets before opening another
    if (remainingPieces.length > 0) {
      let changed = true;
      while (changed) {
        changed = false;
        const stillRemaining = [];
        for (const piece of remainingPieces) {
          let placed = false;
          for (const prevSheet of sheets) {
            const result = findGuillotinePlacement(piece, prevSheet.freeRects);
            if (result) {
              placeOnGuillotineSheet(prevSheet, result.placement, result.rectIndex, result.orientation, cutWidth, splitRule);
              placed = true;
              changed = true;
              break;
            }
          }
          if (!placed) stillRemaining.push(piece);
        }
        remainingPieces = stillRemaining;
      }
    }
  }

  const layouts = sheets.map((s, idx) => ({
    id: idx + 1, width: s.width, height: s.height, pieces: s.pieces
  }));

  return { layouts, unplacedPieces: remainingPieces, unplacedCount: remainingPieces.length };
};

/** Guillotine with MINAS split rule */
export const guillotineMINASOptimize = (allPieces, sheetWidth, sheetHeight, cutWidth, sortStrategy) => {
  return guillotineOptimize(allPieces, sheetWidth, sheetHeight, cutWidth, sortStrategy, SPLIT_RULES.MINAS);
};

/** Guillotine with SLAS split rule */
export const guillotineSLASOptimize = (allPieces, sheetWidth, sheetHeight, cutWidth, sortStrategy) => {
  return guillotineOptimize(allPieces, sheetWidth, sheetHeight, cutWidth, sortStrategy, SPLIT_RULES.SLAS);
};
